import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Briefcase,
  Building2,
  Calendar,
  IndianRupee,
  Clock,
  ShieldCheck,
  Save,
  Loader2,
  Lock,
  Hash,
  MapPin,
  CreditCard
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../components/Common/Select/PaginatedSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import styles from './EmployeeForm.module.css';

const AddEmployee = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterData, setFilterData] = useState({
    departments: [],
    designations: []
  });

  const [formData, setFormData] = useState({
    employee_name: '',
    email: '',
    phone_number: '',
    employee_code: '',
    department_id: '',
    designation_id: '',
    joining_date: '',
    password: '',
    salary: '',
    basic_salary: '',
    pf_amount: '',
    esic_amount: '',
    professional_tax: '',
    status: 1,
    role: '',
    projects: [],
    // New Fields
    pf_applicable: true,
    esic_applicable: false,
    loan_eligible: false,
    max_loan_amount: '',
    shift_start_time: '09:00',
    shift_end_time: '18:00'
  });



  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Prevent negative numbers for numeric inputs
    if (type === 'number' && value < 0) return;

    let val = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: val };

      // PF & ESIC Calculation logic
      if (name === 'salary') {
        const gross = parseFloat(val) || 0;
        const basic = gross * 0.5;

        // PF Calculation (12% of Salary, capped at 15000 salary)
        if (updated.pf_applicable) {
          updated.pf_amount = (Math.min(gross, 15000) * 0.12).toFixed(2);
        } else {
          updated.pf_amount = '0.00';
        }

        // ESIC Applicability & Calculation
        if (gross <= 21000) {
          updated.esic_applicable = true;
          updated.esic_amount = Math.round(gross * 0.0075);
        } else {
          updated.esic_applicable = false;
          updated.esic_amount = '0.00';
        }

        // Professional Tax Calculation: ₹200 if salary >= 12300
        if (gross >= 12300) {
          updated.professional_tax = '200.00';
        } else {
          updated.professional_tax = '0.00';
        }
      }

      if (name === 'max_loan_amount') {
        const loanAmt = parseFloat(val) || 0;
        if (loanAmt > 0) {
          updated.loan_eligible = true;
        }
      }

      if (name === 'pf_applicable') {
        if (val) {
          const gross = parseFloat(updated.salary) || 0;
          updated.pf_amount = (Math.min(gross, 15000) * 0.12).toFixed(2);
        } else {
          updated.pf_amount = '0.00';
        }
      }

      if (name === 'esic_applicable') {
        const gross = parseFloat(updated.salary) || 0;

        if (val && gross <= 21000) {
          updated.esic_amount = Math.round(gross * 0.0075);
        } else {
          // Even if toggle ON, do NOT deduct if salary > 21000
          updated.esic_amount = '0.00';
        }
      }

      if (name === 'loan_eligible') {
        if (!val) {
          updated.max_loan_amount = 0;
        }
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        pf_deduction: formData.pf_amount,
        esic_deduction: formData.esic_amount,
        net_take_home: (
          parseFloat(formData.salary || 0) -
          parseFloat(formData.pf_amount || 0) -
          parseFloat(formData.esic_amount || 0) -
          parseFloat(formData.professional_tax || 0)
        ).toFixed(2)
      };
      const response = await api.post('/employees', dataToSubmit);
      if (response.data.success) {
        toast.success('Employee created successfully');
        navigate('/employees');
      }
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error(error.response?.data?.message || 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['employee-form-layout']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Employees', path: '/employees' },
            { label: 'Add New', path: '/employees/add' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Add New Employee</h1>
              <p>Setup a new organization personnel record with full configuration.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={styles['form-container']}>
              <div className={styles['left-column']}>
                {/* Personal & Professional Info */}
                <div className={styles['form-card']}>
                  <div className={styles['card-header']}>
                    <User size={20} color="#f97316" />
                    <h3>Basic Information</h3>
                  </div>
                  <div className={styles['form-grid']}>
                    <div className={styles['form-group']}>
                      <label><User size={14} /> Full Name</label>
                      <input
                        type="text" name="employee_name" required
                        className={styles['form-input']} placeholder="e.g. John Doe"
                        value={formData.employee_name} onChange={handleInputChange}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><Hash size={14} /> Employee Code</label>
                      <input
                        type="text" name="employee_code" required
                        className={styles['form-input']} placeholder="e.g. EMP-001"
                        value={formData.employee_code} onChange={handleInputChange}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><Mail size={14} /> Email Address</label>
                      <input
                        type="email" name="email" required
                        className={styles['form-input']} placeholder="johndoe@example.com"
                        value={formData.email} onChange={handleInputChange}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><Phone size={14} /> Phone Number</label>
                      <input
                        type="text" name="phone_number" required
                        className={styles['form-input']} placeholder="9XXXXXXXXX"
                        value={formData.phone_number} onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Organization Details */}
                <div className={styles['form-card']}>
                  <div className={styles['card-header']}>
                    <Building2 size={20} color="#3b82f6" />
                    <h3>Department & Role</h3>
                  </div>
                  <div className={styles['form-grid']}>
                    <div className={styles['form-group']}>
                      <label><Building2 size={14} /> Department</label>
                      <PaginatedSelect
                        endpoint="/departments"
                        valueKey="id"
                        labelKey="departement_name"
                        value={formData.department_id}
                        onChange={(val) => setFormData({ ...formData, department_id: val })}
                        placeholder="Select Department"
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><Briefcase size={14} /> Designation</label>
                      <PaginatedSelect
                        endpoint="/designations"
                        valueKey="id"
                        labelKey="designation_name"
                        value={formData.designation_id}
                        onChange={(val) => setFormData({ ...formData, designation_id: val })}
                        placeholder="Select Designation"
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><MapPin size={14} /> Projects Allocated</label>
                      <PaginatedSelect
                        endpoint="/projects-dropdown"
                        valueKey="id"
                        labelKey="project_name"
                        value={formData.projects}
                        onChange={(val) => setFormData({ ...formData, projects: val })}
                        placeholder="Select Projects..."
                        isMulti={true}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><ShieldCheck size={14} /> Role</label>
                      <CustomSelect
                        options={[
                          { value: 'admin', label: 'Admin' },
                          { value: 'user', label: 'User' }
                        ]}
                        value={formData.role ? { value: formData.role, label: formData.role === 'admin' ? 'Admin' : 'User' } : null}
                        onChange={(opt) => setFormData({ ...formData, role: opt.value })}
                        placeholder="Select Role"
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><Calendar size={14} /> Date of Joining</label>
                      <CustomDatePicker
                        selected={formData.joining_date}
                        onChange={(date) => setFormData({ ...formData, joining_date: date.toLocaleDateString('en-CA') })}
                        placeholder="Select Date"
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><Lock size={14} /> System Password</label>
                      <input
                        type="password" name="password" required={true}
                        className={styles['form-input']} placeholder="Min 8 characters"
                        value={formData.password} onChange={handleInputChange}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><IndianRupee size={14} /> Monthly Salary</label>
                      <input
                        type="number" name="salary" required min="0"
                        className={styles['form-input']} placeholder="e.g. 25000"
                        value={formData.salary} onChange={handleInputChange}
                      />
                    </div>
                    <div className={styles['form-group']}>
                      <label><ShieldCheck size={14} /> PF Deduction (12% of Basic)</label>
                      <input
                        type="number" name="pf_amount" readOnly
                        className={styles['form-input']} placeholder="Calculated PF"
                        value={formData.pf_amount}
                        style={{ background: '#f8fafc', fontWeight: '700', color: '#0f172a' }}
                      />
                      <small style={{ fontSize: '10px', color: '#64748b' }}>12% of Salary (capped at ₹15,000 salary), max ₹1,800</small>
                    </div>
                    <div className={styles['form-group']}>
                      <label><ShieldCheck size={14} /> ESIC Deduction (0.75%)</label>
                      <input
                        type="number" name="esic_amount" readOnly
                        className={styles['form-input']} placeholder="Calculated ESIC"
                        value={formData.esic_amount}
                        style={{ background: '#f8fafc', fontWeight: '700', color: '#0f172a' }}
                      />
                      <small style={{ fontSize: '10px', color: '#64748b' }}>0.75% of Salary (Salary ≤ ₹21,000)</small>
                    </div>
                    <div className={styles['form-group']}>
                      <label><ShieldCheck size={14} /> Professional Tax</label>
                      <input
                        type="number" name="professional_tax" readOnly
                        className={styles['form-input']} placeholder="Calculated Tax"
                        value={formData.professional_tax}
                        style={{ background: '#f8fafc', fontWeight: '700', color: '#0f172a' }}
                      />
                      <small style={{ fontSize: '10px', color: '#64748b' }}>₹200 if Salary ≥ ₹12,300</small>
                    </div>
                    <div className={styles['form-group']}>
                      <label><IndianRupee size={14} /> Loan Deduction / EMI</label>
                      <input
                        type="number" name="max_loan_amount" min="0"
                        className={styles['form-input']} placeholder="e.g. 2000"
                        value={formData.max_loan_amount} onChange={handleInputChange}
                      />
                      <small style={{ fontSize: '10px', color: '#64748b' }}>Monthly loan recovery amount</small>
                    </div>
                    <div className={styles['form-group']}>
                      <label><IndianRupee size={14} /> Net Take-Home Salary</label>
                      <input
                        type="number" readOnly
                        className={styles['form-input']}
                        value={(
                          parseFloat(formData.salary || 0) -
                          parseFloat(formData.pf_amount || 0) -
                          parseFloat(formData.esic_amount || 0) -
                          parseFloat(formData.professional_tax || 0)
                        ).toFixed(2)}
                        style={{ background: '#f0fdf4', fontWeight: '800', color: '#16a34a' }}
                      />
                      <small style={{ fontSize: '10px', color: '#64748b' }}>Salary - PF - ESIC - Tax</small>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['right-column']}>
                {/* Shift Configuration */}
                <div className={styles['form-card']}>
                  <div className={styles['card-header']}>
                    <Clock size={20} color="#6366f1" />
                    <h3>Shift Timing</h3>
                  </div>
                  <div className={styles['form-group']} style={{ marginBottom: '20px' }}>
                    <label><Clock size={14} /> Start Time</label>
                    <input
                      type="time" name="shift_start_time"
                      className={styles['form-input']}
                      value={formData.shift_start_time} onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label><Clock size={14} /> End Time</label>
                    <input
                      type="time" name="shift_end_time"
                      className={styles['form-input']}
                      value={formData.shift_end_time} onChange={handleInputChange}
                    />
                  </div>
                </div>

                {/* Compliance & Loan */}
                <div className={styles['form-card']}>
                  <div className={styles['card-header']}>
                    <ShieldCheck size={20} color="#10b981" />
                    <h3>Compliance & Finance Configuration</h3>
                  </div>
                  <div className={styles['toggle-grid']}>
                    <div className={styles['toggle-group']}>
                      <div className={styles['toggle-label']}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ShieldCheck size={16} color="#0f172a" />
                          <span>PF Applicable</span>
                        </div>
                        <span>Provident Fund contributions</span>
                      </div>
                      <label className={styles['switch']}>
                        <input
                          type="checkbox" name="pf_applicable"
                          checked={formData.pf_applicable} onChange={handleInputChange}
                        />
                        <span className={styles['slider']}></span>
                      </label>
                    </div>

                    <div className={styles['toggle-group']}>
                      <div className={styles['toggle-label']}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ShieldCheck size={16} color="#0f172a" />
                          <span>ESIC Applicable</span>
                        </div>
                        <span>Employee State Insurance</span>
                      </div>
                      <label className={styles['switch']}>
                        <input
                          type="checkbox" name="esic_applicable"
                          checked={formData.esic_applicable} onChange={handleInputChange}
                        />
                        <span className={styles['slider']}></span>
                      </label>
                    </div>

                    <div className={styles['toggle-group']}>
                      <div className={styles['toggle-label']}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <IndianRupee size={16} color="#0f172a" />
                          <span>Loan Eligible</span>
                        </div>
                        <span>Allow salary advances/loans</span>
                      </div>
                      <label className={styles['switch']}>
                        <input
                          type="checkbox" name="loan_eligible"
                          checked={formData.loan_eligible} onChange={handleInputChange}
                        />
                        <span className={styles['slider']}></span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles['footer-actions']}>
              <button type="button" className={styles['btn-cancel']} onClick={() => navigate('/employees')}>
                Cancel
              </button>
              <button type="submit" className={styles['btn-save']} disabled={loading}>
                {loading ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
                <span>Create Employee</span>
              </button>
            </div>
          </form>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default AddEmployee;
